import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  Select,
  Paper,
  Theme,
  TextField,
  Typography,
  useMediaQuery,
  MenuItem,
  InputLabel,
} from "@mui/material";
import RichTextEditor from "../components/RichTextEditor/RichTextEditor";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { getPromptByKey, updatePromptByKey } from "./PromptManagementActions";
import { RootState } from "../../store/store";
import { Descendant } from "slate";
import ReactMarkdown from "react-markdown";
import { slateToMarkdown } from "../../common/CMS/SlateToMarkdown/SlateToMarkdown";
import { markdownToSlate } from "../../common/CMS/MarkdownToSlate/MarkdownToSlate";

const PromptManagement = () => {
  const { promptData } = useAppSelector(
    (state: RootState) => state.promptManagment
  );
  const [description, setDescription] = useState<string>("");
  const [originalDescription, setOriginalDescription] = useState<string>("");
  const [promptTemplate, setPromptTemplate] = useState<Descendant[]>([
    {
      type: "paragraph",
      children: [{ text: "" }],
    },
  ]);
  const [originalPromptTemplate, setOriginalPromptTemplate] = useState<
    Descendant[]
  >([
    {
      type: "paragraph",
      children: [{ text: "" }],
    },
  ]);
  const [editorReady, setEditorReady] = useState<boolean>(false);
  const [selectedPromptKey, setSelectedPromptKey] = useState<string>("");
  const [editorKey, setEditorKey] = useState<number>(0);
  const isMobile = useMediaQuery("(max-width:767px)");
  const dispatch = useAppDispatch();

  // Helper function to create a proper deep copy of Slate nodes
  const deepCopyNodes = (nodes: Descendant[]): Descendant[] => {
    return JSON.parse(JSON.stringify(nodes));
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDescription(e.target.value);
  };

  const handlePromptTemplateChange = (value: Descendant[]) => {
    setPromptTemplate(value);
  };

  const handleSubmit = () => {
    const updatePromptRequestBody = {
      promptKey: selectedPromptKey,
      promptTemplate: slateToMarkdown(promptTemplate),
      description,
    };
    dispatch(updatePromptByKey(updatePromptRequestBody));
    // After successful submission, update original values
    setOriginalDescription(description);
    setOriginalPromptTemplate(deepCopyNodes(promptTemplate));
  };

  const handleCancel = () => {
    // Revert to original values
    setDescription(originalDescription);
    // For Slate editor, we'll reset it completely to avoid state issues
    setPromptTemplate(deepCopyNodes(originalPromptTemplate));
    // Force re-render the editor with a new key
    setEditorKey((prev) => prev + 1);
  };

  useEffect(() => {
    if (selectedPromptKey) {
      dispatch(getPromptByKey(selectedPromptKey));
    }
  }, [selectedPromptKey, dispatch]);

  useEffect(() => {
    if (promptData) {
      const newDescription = promptData.description?.trim()
        ? promptData.description
        : "";
      setDescription(newDescription);
      setOriginalDescription(newDescription);
    }
  }, [promptData]);

  useEffect(() => {
    if (promptData && promptData.promptTemplate) {
      try {
        const parsedTemplate = markdownToSlate(promptData.promptTemplate);
        setPromptTemplate(parsedTemplate);
        setOriginalPromptTemplate(deepCopyNodes(parsedTemplate));
      } catch (error) {
        const fallbackTemplate = [
          {
            type: "paragraph",
            children: [{ text: promptData.promptTemplate || "" }],
          },
        ] as Descendant[];

        setPromptTemplate(fallbackTemplate);
        setOriginalPromptTemplate(deepCopyNodes(fallbackTemplate));
      }
    } else {
      const emptyTemplate = [
        {
          type: "paragraph",
          children: [{ text: "" }],
        },
      ] as Descendant[];

      setPromptTemplate(emptyTemplate);
      setOriginalPromptTemplate(deepCopyNodes(emptyTemplate));
    }
    setEditorReady(true);
  }, [promptData]);

  useEffect(() => {
    setEditorReady(false);
  }, [selectedPromptKey]);

  return (
    <Box sx={{ padding: "20px" }}>
      <Typography
        variant="h5"
        sx={{
          background: (theme: Theme) =>
            `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          fontWeight: "bold",
        }}
      >
        Prompt Management
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          alignItems: "center",
          gap: 2,
          mb: 3,
        }}
      >
        <Typography
          sx={{
            fontWeight: "bold",
            fontSize: "1.2rem",
            minWidth: "200px",
          }}
        >
          Select the prompt key:
        </Typography>

        <FormControl sx={{ minWidth: 300 }} variant="outlined">
          <InputLabel id="prompt-key-label">Select a prompt key</InputLabel>
          <Select
            labelId="prompt-key-label"
            id="prompt-key-select"
            value={selectedPromptKey}
            onChange={(e) => setSelectedPromptKey(e.target.value as string)}
            label="Select a prompt key"
            sx={{
              borderRadius: 1,
              height: "50px",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#d0d0d0",
              },
            }}
          >
            <MenuItem value="" disabled>
              <em>Select a prompt key</em>
            </MenuItem>
            <MenuItem value="chat-prompt">Chat Prompt</MenuItem>
            <MenuItem value="user-summary">User Summary</MenuItem>
            <MenuItem value="sentiment-analysis">Sentiment Analysis</MenuItem>
            <MenuItem value="customer-story">Customer Story</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Paper
        elevation={0}
        sx={{
          padding: 3,
          borderRadius: 2,
          border: "1px solid #e0e0e0",
          backgroundColor: "#fff",
        }}
      >
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
            Prompt Description
          </Typography>
          <TextField
            multiline
            rows={4}
            value={description}
            onChange={handleDescriptionChange}
            fullWidth
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 1,
              },
            }}
          />
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
            Prompt Template
          </Typography>
          <RichTextEditor
            key={`${selectedPromptKey}-${
              editorReady ? "ready" : "loading"
            }-${editorKey}`}
            value={promptTemplate}
            onChange={handlePromptTemplateChange}
            fontSize="16px"
            label="Prompt Template"
            required={true}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            mt: 4,
          }}
        >
          <Button
            variant="outlined"
            onClick={handleCancel}
            sx={{
              borderRadius: 1,
              padding: "8px 24px",
              textTransform: "none",
              fontWeight: 500,
            }}
          >
            Reset
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            sx={{
              borderRadius: 1,
              padding: "8px 24px",
              textTransform: "none",
              fontWeight: 500,
            }}
          >
            Save
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default PromptManagement;
